import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Plus, Trash2, Loader2, ChevronDown } from 'lucide-react';
import {
  Button,
  Input,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  getPatientAllergies,
  createPatientAllergy,
  createUserDefinedPatientAllergy,
  deletePatientAllergy,
  searchAllergies,
  getUserDefinedAllergies,
  updatePatient,
  client,
} from '../services/api';
import { useToast } from '../hooks/useToastContext';
import type {
  PatientResponse,
  PatientAllergyResponse,
} from '../services/api/types.gen';

interface PatientAllergiesProps {
  patient: PatientResponse;
}

interface AllergyFormData {
  allergy_name: string;
  reaction: string;
}

interface AllergyOption {
  id: string;
  name: string;
}

const PatientAllergies: React.FC<PatientAllergiesProps> = ({ patient }) => {
  const { success } = useToast();
  const [patientAllergies, setPatientAllergies] = useState<
    PatientAllergyResponse[]
  >([]);
  const [allergyOptions, setAllergyOptions] = useState<AllergyOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<AllergyOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nkaUpdating, setNkaUpdating] = useState(false);
  const [nkaChecked, setNkaChecked] = useState(
    patient.no_known_allergies ?? false
  );
  const [addExternalMode, setAddExternalMode] = useState(false);
  const [externalAllergyOptions, setExternalAllergyOptions] = useState<
    AllergyOption[]
  >([]);
  const [filteredExternalOptions, setFilteredExternalOptions] = useState<
    AllergyOption[]
  >([]);
  const [loadingExternalOptions, setLoadingExternalOptions] = useState(true);
  const [selectedAllergyId, setSelectedAllergyId] = useState<string | null>(
    null
  );
  const [selectedExternalAllergyId, setSelectedExternalAllergyId] = useState<
    string | null
  >(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const allergySearchRequestRef = useRef(0);

  const hasAllergies = patientAllergies.length > 0;

  useEffect(() => {
    setNkaChecked(patient.no_known_allergies ?? false);
  }, [patient.no_known_allergies]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<AllergyFormData>({
    defaultValues: {
      allergy_name: '',
      reaction: '',
    },
  });

  const allergyNameValue = watch('allergy_name');

  const fetchUserDefinedAllergyOptions = async () => {
    try {
      setLoadingExternalOptions(true);
      const response = await getUserDefinedAllergies({ client });
      if (response?.data) {
        const options = response.data.map(allergy => ({
          id: allergy.id,
          name: allergy.name,
        }));
        setExternalAllergyOptions(options);
        setFilteredExternalOptions(options);
      }
    } catch (err) {
      console.error('Error fetching user-defined allergy options:', err);
    } finally {
      setLoadingExternalOptions(false);
    }
  };

  useEffect(() => {
    fetchAllergies();
    fetchUserDefinedAllergyOptions();
  }, [patient.id]);

  const fetchAllergies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPatientAllergies({
        path: { patient_id: patient.id },
        client,
      });
      if (response?.data) {
        setPatientAllergies(response.data);
      }
    } catch (err) {
      console.error('Error fetching patient allergies:', err);
      setError('Failed to load allergies');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllergyOptions = async (searchTerm = '') => {
    const requestId = ++allergySearchRequestRef.current;

    try {
      setLoadingOptions(true);
      const response = await searchAllergies({
        client,
        query: {
          limit: 50,
          ...(searchTerm ? { search: searchTerm } : {}),
        },
      });
      if (response?.data) {
        const options = response.data.map(allergy => ({
          id: allergy.id,
          name: allergy.name,
        }));

        if (requestId !== allergySearchRequestRef.current) {
          return;
        }

        setAllergyOptions(options);
        setFilteredOptions(options);
      }
    } catch (err) {
      console.error('Error fetching allergy options:', err);
    } finally {
      if (requestId === allergySearchRequestRef.current) {
        setLoadingOptions(false);
      }
    }
  };

  // Search standard allergies from the API as the user types.
  useEffect(() => {
    if (!isDialogOpen || addExternalMode) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void fetchAllergyOptions(allergyNameValue.trim());
      setHighlightedIndex(0);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [allergyNameValue, addExternalMode, isDialogOpen]);

  // Filter user-defined options locally.
  useEffect(() => {
    if (!allergyNameValue) {
      setFilteredExternalOptions(externalAllergyOptions);
      setHighlightedIndex(0);
      return;
    }

    const query = allergyNameValue.toLowerCase();
    const filtered = externalAllergyOptions.filter(option =>
      option.name.toLowerCase().includes(query)
    );
    setFilteredExternalOptions(filtered);
    setHighlightedIndex(0);
  }, [allergyNameValue, externalAllergyOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDialog = () => {
    reset();
    setAddExternalMode(false);
    setSelectedAllergyId(null);
    setSelectedExternalAllergyId(null);
    setShowDropdown(false);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && !submitting) {
      setIsDialogOpen(false);
      reset();
      setAddExternalMode(false);
      setSelectedAllergyId(null);
      setSelectedExternalAllergyId(null);
      setShowDropdown(false);
    }
  };

  const onSubmit = async (data: AllergyFormData) => {
    if (!data.allergy_name || !data.reaction) return;

    try {
      setSubmitting(true);
      setError(null);

      if (addExternalMode) {
        if (selectedExternalAllergyId) {
          await createPatientAllergy({
            path: { patient_id: patient.id },
            body: {
              allergy_id: selectedExternalAllergyId,
              reaction: data.reaction,
            },
            client,
          });
        } else {
          await createUserDefinedPatientAllergy({
            path: { patient_id: patient.id },
            body: {
              allergy_name: data.allergy_name,
              reaction: data.reaction,
            },
            client,
          });
        }
      } else {
        const selectedAllergy =
          allergyOptions.find(a => a.id === selectedAllergyId) ??
          allergyOptions.find(
            a => a.name.toLowerCase() === data.allergy_name.toLowerCase()
          );

        if (!selectedAllergy) {
          setError(
            'Please select an allergy from the list or use "Not in list" mode'
          );
          return;
        }

        await createPatientAllergy({
          path: { patient_id: patient.id },
          body: {
            allergy_id: selectedAllergy.id,
            reaction: data.reaction,
          },
          client,
        });
      }

      reset();
      setSelectedAllergyId(null);
      setSelectedExternalAllergyId(null);
      setIsDialogOpen(false);
      await fetchAllergies();
    } catch (err) {
      console.error('Error creating allergy:', err);
      setError('Failed to add allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (patientAllergyId: string, allergyName: string) => {
    try {
      setDeletingId(patientAllergyId);
      setError(null);
      await deletePatientAllergy({
        path: { patient_allergy_id: patientAllergyId },
        client,
      });
      success(`Successfully deleted patient allergy: ${allergyName}`);
      await fetchAllergies();
    } catch (err) {
      console.error('Error deleting allergy:', err);
      setError('Failed to delete allergy');
    } finally {
      setDeletingId(null);
    }
  };

  const onNkaChange = async (checked: boolean | 'indeterminate') => {
    const value = checked === true;
    try {
      setNkaUpdating(true);
      setError(null);
      await updatePatient({
        path: { patient_id: patient.id },
        body: { no_known_allergies: value },
        client,
      });
      setNkaChecked(value);
      success(
        value ? 'Marked as no known allergies' : 'Cleared no known allergies'
      );
    } catch (err) {
      console.error('Error updating no known allergies:', err);
      setError('Failed to update no known allergies');
    } finally {
      setNkaUpdating(false);
    }
  };

  // Allergen combobox used inside the dialog
  const renderAllergenInput = () => {
    const options = addExternalMode ? filteredExternalOptions : filteredOptions;
    const isOptionsLoading = addExternalMode
      ? loadingExternalOptions
      : loadingOptions;

    return (
      <div className='relative' ref={dropdownRef}>
        <Controller
          name='allergy_name'
          control={control}
          rules={{ required: 'Allergen is required' }}
          render={({ field }) => (
            <>
              <div className='relative'>
                <Input
                  {...field}
                  id='allergy_name'
                  placeholder='Type or select allergen...'
                  disabled={submitting || (addExternalMode && isOptionsLoading)}
                  inputSize='default'
                  onChange={e => {
                    field.onChange(e);
                    if (addExternalMode) {
                      setSelectedExternalAllergyId(null);
                    } else {
                      setSelectedAllergyId(null);
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={e => {
                    if (
                      e.key === 'Enter' &&
                      options.length > 0 &&
                      showDropdown
                    ) {
                      e.preventDefault();
                      const selected = options[highlightedIndex];
                      setValue('allergy_name', selected.name);
                      if (addExternalMode) {
                        setSelectedExternalAllergyId(selected.id);
                      } else {
                        setSelectedAllergyId(selected.id);
                      }
                      setShowDropdown(false);
                    } else if (e.key === 'Escape') {
                      setShowDropdown(false);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setShowDropdown(true);
                      setHighlightedIndex(prev =>
                        prev < options.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
                    }
                  }}
                />
                <ChevronDown className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none' />
              </div>
              {showDropdown && options.length > 0 && (
                <div className='absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-auto'>
                  {options.map((option, index) => (
                    <button
                      key={option.id}
                      type='button'
                      className={`w-full px-3 py-1.5 text-left text-sm cursor-pointer ${
                        index === highlightedIndex
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent/50'
                      }`}
                      onClick={() => {
                        setValue('allergy_name', option.name);
                        if (addExternalMode) {
                          setSelectedExternalAllergyId(option.id);
                        } else {
                          setSelectedAllergyId(option.id);
                        }
                        setShowDropdown(false);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        />
      </div>
    );
  };

  return (
    <div>
      {/* Top bar: NKA checkbox + Add button */}
      <div className='flex items-center justify-between mb-4'>
        <div
          className='flex items-center gap-2'
          title={
            hasAllergies
              ? 'Cannot select if the patient has any allergies'
              : undefined
          }
        >
          <Checkbox
            id='nka'
            checked={nkaChecked}
            disabled={hasAllergies || nkaUpdating}
            onCheckedChange={onNkaChange}
            aria-label='NKA (Food / Drug / Latex / Environmental)'
          />
          <label
            htmlFor='nka'
            className='text-sm font-medium leading-none cursor-pointer select-none'
          >
            NKA (No Known Allergies)
          </label>
        </div>
        {!nkaChecked && (
          <Button size='sm' onClick={openDialog}>
            <Plus className='h-3.5 w-3.5 mr-1.5' />
            Add Allergy
          </Button>
        )}
      </div>

      {/* Allergy list */}
      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : error ? (
        <div className='text-center py-8 text-sm text-muted-foreground'>
          {error}
        </div>
      ) : nkaChecked ? (
        <div className='text-center py-8 text-sm text-muted-foreground'>
          Patient has no known allergies
        </div>
      ) : patientAllergies.length === 0 ? (
        <div className='text-center py-8 text-sm text-muted-foreground'>
          No allergies on record
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Allergen</TableHead>
              <TableHead>Reaction</TableHead>
              <TableHead className='w-[60px]' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {patientAllergies.map(allergy => (
              <TableRow key={allergy.id}>
                <TableCell className='font-medium'>
                  {allergy.allergy?.name || 'Unknown'}
                </TableCell>
                <TableCell>{allergy.reaction}</TableCell>
                <TableCell>
                  <Button
                    variant='ghost'
                    size='sm'
                    disabled={deletingId === allergy.id}
                    onClick={() =>
                      onDelete(allergy.id, allergy.allergy?.name || 'Unknown')
                    }
                  >
                    {deletingId === allergy.id ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <Trash2 className='h-4 w-4 text-destructive' />
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Add Allergy Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Add Allergy</DialogTitle>
            <DialogDescription>
              {addExternalMode
                ? 'Add an allergy not found in the standard list.'
                : 'Search for an allergen and enter the reaction.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 py-2'>
            {/* Mode toggle */}
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={() => {
                  setAddExternalMode(false);
                  setSelectedAllergyId(null);
                  setSelectedExternalAllergyId(null);
                  reset();
                }}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  !addExternalMode
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                From list
              </button>
              <button
                type='button'
                onClick={() => {
                  setAddExternalMode(true);
                  setSelectedAllergyId(null);
                  setSelectedExternalAllergyId(null);
                  reset();
                }}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  addExternalMode
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Not in list
              </button>
            </div>

            {addExternalMode && (
              <p className='text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2'>
                Make sure to check this allergy is not in our system first.
              </p>
            )}

            {/* Allergen field */}
            <div>
              <Label className='text-xs text-muted-foreground mb-1 block'>
                Allergen
              </Label>
              {renderAllergenInput()}
              {errors.allergy_name && (
                <p className='text-xs text-destructive mt-1'>
                  {errors.allergy_name.message}
                </p>
              )}
            </div>

            {/* Reaction field */}
            <div>
              <Label className='text-xs text-muted-foreground mb-1 block'>
                Reaction
              </Label>
              <Input
                id='reaction'
                placeholder='Enter reaction...'
                disabled={submitting}
                inputSize='default'
                {...register('reaction', {
                  required: 'Reaction is required',
                })}
              />
              {errors.reaction && (
                <p className='text-xs text-destructive mt-1'>
                  {errors.reaction.message}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleDialogClose(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={submitting || (!addExternalMode && loadingOptions)}
              >
                {submitting && (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                )}
                Add Allergy
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientAllergies;
